import { useFlag } from '@calendly/feature-check-react'
import { useFormContext } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { useEffect, useMemo, useRef } from 'react'

import { useEventType, useEventTypeContext } from 'features/event_type_provider'
import { isNotetakerEligibleLocationKind } from 'features/growth_experiments'
import { useGetMeetingIntelligenceUserSettingsQuery } from 'features/meeting_intelligence'

import { Summary } from './summary'
import { AccordionPanel } from '../../../../components/accordion'
import { LocationField } from '../../location'

const getAutoRecordCalendlyEnabled = (miSettings) => {
  if (!miSettings) return true

  if (typeof miSettings.autoRecordV3Calendly === 'boolean') return miSettings.autoRecordV3Calendly
  if (typeof miSettings.autoRecordV2Calendly === 'string') return miSettings.autoRecordV2Calendly !== 'none'

  return true
}

const getMiCalendlyIncludeList = (miSettings) => (
  miSettings?.autoRecordV3CalendlyIncludeList
  || miSettings?.autoRecordV2CalendlyIncludeList
  || []
)

const isEventTypeEnabledViaMi = ({ eventTypeId, miSettings }) => {
  if (!eventTypeId || !miSettings) return false

  const miEnabled = Boolean(miSettings.miEnabled)
  const autoRecordCalendlyEnabled = getAutoRecordCalendlyEnabled(miSettings)
  const includeList = getMiCalendlyIncludeList(miSettings)
  const eventTypeIdAsString = String(eventTypeId)
  const isEtInIncludeList = includeList.some((id) => String(id) === eventTypeIdAsString)

  return miEnabled && autoRecordCalendlyEnabled && isEtInIncludeList
}

const hasEligibleNotetakerLocation = (locationConfigurations = [], hasMsTeamsFlag = false) => (
  locationConfigurations.some((locationConfiguration) => (
    locationConfiguration
    && !locationConfiguration._destroy
    && isNotetakerEligibleLocationKind(locationConfiguration.kind, hasMsTeamsFlag)
  ))
)

const NotetakerSummaryHydrator = () => {
  const eventType = useEventType()
  const {
    values,
    setFieldValue,
  } = useFormContext()
  const {
    data: meetingIntelligenceSettings,
    isLoading: isLoadingMiSettings,
    isFetching: isFetchingMiSettings,
  } = useGetMeetingIntelligenceUserSettingsQuery()
  const hasMsTeamsFlag = useFlag('ms_teams_notetaker')
  const hasNotetakerFieldValue = typeof values?.notetakerEnabledForEt === 'boolean'
  const hasEligibleLocation = useMemo(
    () => hasEligibleNotetakerLocation(values?.locationConfigurations, hasMsTeamsFlag),
    [values?.locationConfigurations, hasMsTeamsFlag]
  )

  useEffect(() => {
    if (hasNotetakerFieldValue) return
    if (!eventType?.id || !hasEligibleLocation) return
    if (isLoadingMiSettings || isFetchingMiSettings) return

    if (isEventTypeEnabledViaMi({
      eventTypeId: eventType.id,
      miSettings: meetingIntelligenceSettings,
    })) {
      setFieldValue('notetakerEnabledForEt', true)
    }
  }, [
    eventType?.id,
    hasEligibleLocation,
    hasNotetakerFieldValue,
    isFetchingMiSettings,
    isLoadingMiSettings,
    meetingIntelligenceSettings,
    setFieldValue,
  ])

  return null
}

export const LocationPanel = ({ isLocked, isReadOnly, expand, ...props }) => {
  const { isCreation } = useEventTypeContext()
  const prevExpandRef = useRef(expand)
  const hasClosedLocationSectionRef = useRef(false)

  useEffect(() => {
    if (isCreation && prevExpandRef.current && !expand) {
      hasClosedLocationSectionRef.current = true
    }
    prevExpandRef.current = expand
  }, [expand, isCreation])

  const showNoLocationWarningOnReopen = isCreation && hasClosedLocationSectionRef.current

  return (
    <>
      <NotetakerSummaryHydrator />
      <AccordionPanel
        titleId="location-label"
        title={I18n.t('publisher.event_types.location.location')}
        subheader={<Summary isReadOnly={isReadOnly || isLocked} />}
        expand={expand}
        {...props}
      >
        <LocationField
          isLocked={isLocked}
          isReadOnly={isReadOnly || isLocked}
          showNoLocationWarningOnReopen={showNoLocationWarningOnReopen}
        />
      </AccordionPanel>
    </>
  )
}
